import * as React from 'react'
import { useContext } from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { ArcadeIcon } from '@arcade/web/icons/react'

import { useDebounce } from '../../../hooks/useDebounce'
import { truncate } from '../../../styles'
import { spacing, ThemeVariant } from '../../../theme'
import LoadingIcon from '../../LoadingIcon/LoadingIcon'
import { CashDesignSystemContext, ThemeVariantProvider } from '../../Provider'
import { ProgressCircular } from '../ProgressCircular/ProgressCircular'
import { focusRingStyles, getVariantStyles } from './arcade-button-styles'

export type ButtonSize = 'default' | 'cta' | 'compact' | 'icon'
export type ButtonProminence = 'prominent' | 'standard' | 'subtle'

export type ButtonProps = {
  ariaLabel?: string
  disabled?: boolean
  Icon?: ArcadeIcon
  isDestructive?: boolean
  isLoading?: boolean
  linesToTruncate?: number
  loadingDelay?: number
  /** Defaults to 'none' for full width buttons */
  maxWidth?: 'small' | 'none'
  prominence?: ButtonProminence
  size?: ButtonSize
  as?: React.ElementType
} & React.ButtonHTMLAttributes<HTMLButtonElement>

type StyleProps = Pick<ButtonProps, 'prominence' | 'size' | 'linesToTruncate' | 'isDestructive' | 'maxWidth'> & {
  isFunctionallyDisabled?: boolean
  isFunctionallyLoading?: boolean
  isVisuallyDisabled?: boolean
  isVisuallyLoading?: boolean
  size: ButtonSize
  themeVariant: ThemeVariant
}

const ButtonContainer = styled.button<StyleProps>`
  position: relative;
  display: grid;
  background: transparent;
  border: 0;
  width: 100%;
  padding: ${({ theme: { spacing } }) => spacing.empty};
  outline: none;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  user-select: none;

  ${({ maxWidth }) => {
    switch (maxWidth) {
      case 'small':
        return css`
          max-width: 14rem;
        `
      default:
        return
    }
  }}

  ${({ size }) => {
    switch (size) {
      case 'default':
      case 'compact':
      case 'icon':
        return css`
          width: auto;
        `
      default:
        return
    }
  }}

  ${({ isVisuallyLoading }) =>
    isVisuallyLoading &&
    css`
      cursor: default;
    `}

    ${({ isVisuallyDisabled }) =>
    isVisuallyDisabled &&
    css`
      cursor: not-allowed;
    `}

    /* focus ring styles */
    ${({ theme, size }) => focusRingStyles({ theme, size })}
`

export const StyledButton = styled.span<StyleProps>`
  /* text */
  font-family: inherit;
  text-align: center;

  /* layout */
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;

  /* motion */
  ${props => props.theme.motion.standardTransition}
  transition-property: transform, background, color, opacity;

  border-radius: ${({ theme }) => theme.border.radius.pill};
  cursor: pointer;

  ${({ linesToTruncate }) =>
    linesToTruncate &&
    linesToTruncate > 1 &&
    css`
      ${truncate(linesToTruncate)}
      height: auto;
      max-height: initial;
      border-radius: 2rem;
      padding: ${spacing.xxs} ${spacing.default};
    `}

  /* apply variant styles */
  ${({ isFunctionallyDisabled, isVisuallyDisabled, theme, prominence, size, isDestructive, themeVariant }) =>
    getVariantStyles({
      isFunctionallyDisabled,
      isVisuallyDisabled,
      theme,
      prominence,
      size,
      isDestructive,
      themeVariant,
    })}
`

const LoadingPosition = styled.span`
  position: absolute;
  display: flex;
`

const StyledChildren = styled.span<{ hasIcon: boolean; isLoading: boolean }>`
  ${({ hasIcon }) => hasIcon && `padding-left: ${spacing.xs};`}
  ${({ isLoading }) => isLoading && 'visibility: hidden;'}
  display: flex;
`

const IconContainer = styled.span<{ isLoading: boolean }>`
  ${({ isLoading }) => isLoading && 'visibility: hidden;'}
  display: flex;
`

// Inner component that reads theme from context (monochrome when wrapped by ArcadeButton)
const ArcadeButtonInner = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      ariaLabel,
      children,
      disabled,
      Icon,
      isDestructive,
      isLoading,
      linesToTruncate,
      loadingDelay = 0,
      maxWidth = 'none',
      prominence,
      size = 'default',
      ...rest
    },
    ref
  ) => {
    const {
      theme: { themeVariant },
    } = useContext(CashDesignSystemContext)

    const isFunctionallyLoading = isLoading
    const isVisuallyLoading = useDebounce(isLoading, isLoading ? loadingDelay : 0)

    const isFunctionallyDisabled = disabled || isLoading
    const isVisuallyDisabled = disabled

    // According to figma, compact buttons should have a smaller icon (16) while default and cta should have a bigger icon (24)
    const iconSize = size === 'compact' ? 16 : 24

    const styleProps: StyleProps = {
      isDestructive,
      isFunctionallyDisabled,
      isFunctionallyLoading,
      isVisuallyDisabled,
      isVisuallyLoading,
      linesToTruncate,
      maxWidth,
      prominence,
      size,
      themeVariant,
    }

    // For the monochrome theme variant, we want to use the progress circular component for the loading state
    // TODO(dominicy): remove this once we are fully migrated to the progress circular component across the app
    const loadingIcon =
      themeVariant === 'monochrome' ? (
        <ProgressCircular size="small" isDestructive={isDestructive} buttonProminence={prominence} />
      ) : (
        <LoadingIcon size={24} color="current" isDestructive={isDestructive} buttonProminence={prominence} />
      )

    return (
      <ButtonContainer
        ref={ref}
        {...styleProps}
        disabled={isFunctionallyDisabled}
        aria-label={ariaLabel}
        type="button"
        {...rest}
      >
        <StyledButton {...styleProps}>
          {isVisuallyLoading && <LoadingPosition>{loadingIcon}</LoadingPosition>}
          {Icon && (
            <IconContainer isLoading={!!isVisuallyLoading}>
              <Icon data-testid="arcade-button-icon" size={iconSize} />
            </IconContainer>
          )}
          {size !== 'icon' && (
            <StyledChildren isLoading={!!isVisuallyLoading} hasIcon={!!Icon}>
              {children}
            </StyledChildren>
          )}
        </StyledButton>
      </ButtonContainer>
    )
  }
)

// Keep button Buttoncontainer to avoid unclickable edges when using this component as an `a` tag caused by `scale` property
// Wrapped in ThemeVariantProvider so every ArcadeButton renders as monochrome regardless of parent theme.
const ArcadeButton = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => (
  <ThemeVariantProvider themeVariant="monochrome">
    <ArcadeButtonInner ref={ref} {...props} />
  </ThemeVariantProvider>
))

export default ArcadeButton
